// @flow

import _ from 'lodash';

import { ObservableBusy } from 'observable-busy';

import { modeTypes } from './dictionary';
import { DefaultDataModel } from './data-models';
import {
	DefaultController,
	OsagoController,
	KaskoController,
} from './controllers';
import { ApiService, RestApiService } from './services/api';
import TimeoutService from './services/timeout';
import { DefaultViewModel, DmsViewModel, OsagoViewModel } from './view-models';

import * as templates from '../templates/*';

import type {
	IController,
	CreationOptions,
} from './contracts';


class Factory implements IFactory {
	constructor (options: CreationOptions) {
		this._options = _.defaultsDeep({}, options, {
			mode: modeTypes.default,
			busy: undefined,
			sendSmsImmediately: true,
			timeout: {
				enabled: true,
				base: 1 * 60,
				increment: 2 * 60,
				serverTime: undefined,
				resetTime: 1 * 60 * 60,
			},
			descendingSettings: {},
			additionalCss: {},
			checkboxesOptions: {},
		});

		this.busy = new ObservableBusy();
	}

	createApiService = () => {
		switch (this._options.mode) {
			case modeTypes.osago:
				return new ApiService();
			case modeTypes.default:
			default:
				return new RestApiService();
		}
	}

	createTimeoutService = options => {
		switch (this._options.mode) {
			case modeTypes.default:
			default:
				return new TimeoutService(options);
		}
	}

	createDataModel = options => {
		switch (this._options.mode) {
			case modeTypes.default:
			default:
				return new DefaultDataModel(options);
		}
	}

	createViewModel = options => {
		switch (this._options.mode) {
			case modeTypes.osago:
				return new OsagoViewModel({
					...options,
					descendingSettings: this._options.descendingSettings,
				});
			case modeTypes.dms:
				return new DmsViewModel({
					...options,
					checkboxesOptions: this._options.checkboxesOptions,
				});
			case modeTypes.default:
			default:
				return new DefaultViewModel(options);
		}
	}

	createController = options => {
		switch (this._options.mode) {
			case modeTypes.osago:
				return new OsagoController(options);
			case modeTypes.kasko:
				return new KaskoController(options);
			case modeTypes.default:
			default:
				return new DefaultController(options);
		}
	}

	createTemplate = () => {
		switch (this._options.mode) {
			case modeTypes.dms:
				return templates[`${modeTypes.dms}-template`];
			case modeTypes.default:
			default:
				return templates[`${modeTypes.default}-template`];
		}
	}
}

export function create (options: CreationOptions): IController {
	const factory = new Factory(options);

	const dataModel = factory.createDataModel({
		busy: factory.busy,
	});

	const template = factory.createTemplate();

	const apiService = factory.createApiService();

	const timeoutService = factory.createTimeoutService(factory._options.timeout);

	const viewModel = factory.createViewModel({
		dataModel,
		busy: factory.busy,
		timer: timeoutService.timer,
	});

	const controller = factory.createController({
		dataModel,
		viewModel,
		template,
		api: apiService,
		busy: factory.busy,
		outerBusy: factory._options.busy,
		timeoutService,
		sendSmsImmediately: factory._options.sendSmsImmediately,
		descendingSettings: options.descendingSettings,
		additionalCss: options.additionalCss,
	});

	return controller;
}
